import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { TargetType } from '../../services/learningApi';
import {
  HealthState,
  ModelHealth,
  OnFailure,
  ReliabilityDescription,
  ReliabilitySettings,
  reliabilityApi,
} from '../../services/reliabilityApi';

interface PanelProps {
  targetType: TargetType;
  targetId: string;
  actAsPublic?: boolean;
  onSaved?: (desc: ReliabilityDescription) => void;
  onLoaded?: (desc: ReliabilityDescription) => void;
  /** 'dialog' renders DialogContent + DialogActions; 'inline' renders a plain block with its own buttons. */
  layout?: 'dialog' | 'inline';
  onClose?: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  targetType: TargetType;
  targetId: string;
  targetName?: string;
  actAsPublic?: boolean;
  onSaved?: (desc: ReliabilityDescription) => void;
}

const LIMIT_SUMMARY: Record<string, string> = { relaxed: 'relaxed', balanced: 'balanced', strict: 'strict' };

/** Short, human summary of saved settings, e.g. ["Learned time limit (balanced)", "Switches to a similar model, up to 2 backups"]. */
export function summarizeReliability(s: ReliabilitySettings): string[] {
  const parts: string[] = [];
  if (s.time_limit.mode === 'fixed' && s.time_limit.seconds) parts.push(`${s.time_limit.seconds}s time limit`);
  else if (s.time_limit.mode === 'auto') parts.push(`Learned time limit (${LIMIT_SUMMARY[s.time_limit.preset] || s.time_limit.preset})`);
  else parts.push('No time limit');
  const n = s.max_retries;
  if (s.on_failure === 'fail') parts.push('Returns the first error');
  else if (s.on_failure === 'retry') parts.push(`Retries the same model, up to ${n}×`);
  else if (s.on_failure === 'fallback') parts.push(`Switches to a similar model, up to ${n} backup${n > 1 ? 's' : ''}`);
  else parts.push('Standard backups');
  if (s.hedge.enabled && s.on_failure !== 'fail') parts.push('Backs up slow calls');
  if (s.model_choice?.mode === 'spread') {
    const count = s.model_choice.models.length;
    parts.push(count ? `Spreads calls across ${count} models` : 'Spreads each step across similar models');
  }
  return parts;
}

const FAILURE_OPTIONS: { value: OnFailure; label: string; help: string }[] = [
  {
    value: 'standard',
    label: 'Standard (current behaviour)',
    help: "Try this function's other allowed models, then up to 3 general backups, one after another.",
  },
  { value: 'fail', label: 'Return the error', help: 'Stop at the first failure and return the error. No extra attempts or cost.' },
  {
    value: 'retry',
    label: 'Retry the same model',
    help: 'Retry temporary failures (timeouts, rate limits, provider errors) with a short backoff. Request errors are returned right away.',
  },
  {
    value: 'fallback',
    label: 'Switch to a healthy similar model',
    help: "Move to a model with the same capabilities that isn't degraded right now. Automatic picks never cost more than the original.",
  },
];

const PRESET_LABELS: Record<string, string> = {
  relaxed: 'Relaxed: only stop calls far slower than your slowest normal runs',
  balanced: 'Balanced: stop calls much slower than usual (recommended)',
  strict: 'Strict: fail fast; may cut off the occasional slow-but-fine call',
};

const HEALTH_COLOR: Record<HealthState, 'success' | 'warning' | 'error'> = { healthy: 'success', degraded: 'warning', down: 'error' };

const fmt = (s?: number | null) => (s === undefined || s === null ? '–' : s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`);

export const HealthChip: React.FC<{ model: string; health?: ModelHealth; role?: string }> = ({ model, health, role }) => {
  const noData = !health || health.calls === 0;
  const state = health?.state || 'healthy';
  const title = noData
    ? 'No calls in the last 5 minutes'
    : `${health!.calls} calls in the last 5 minutes${health!.avg_latency_s ? `, ${fmt(health!.avg_latency_s)} average` : ''}${health!.reason ? `. ${health!.reason}` : ''}`;
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        variant="outlined"
        color={noData ? 'default' : HEALTH_COLOR[state]}
        label={`${model}${role ? ` · ${role}` : ''}${noData ? '' : state === 'healthy' ? '' : ` · ${state}`}`}
      />
    </Tooltip>
  );
};

const Section: React.FC<{ title: string; help?: string; children: React.ReactNode }> = ({ title, help, children }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    {help && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {help}
      </Typography>
    )}
    {children}
  </Box>
);

export const ReliabilityPanel: React.FC<PanelProps> = ({ targetType, targetId, actAsPublic, onSaved, onLoaded, layout = 'inline', onClose }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => reliabilityApi(getIdToken, !!actAsPublic), [getIdToken, actAsPublic]);
  const [desc, setDesc] = useState<ReliabilityDescription | null>(null);
  const [draft, setDraft] = useState<ReliabilitySettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get(targetType, targetId);
      setDesc(d);
      setDraft(d.settings);
      onLoaded?.(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // onLoaded is a notification; re-running the load when a parent re-renders would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, targetType, targetId]);

  useEffect(() => {
    setNotice(null);
    load();
  }, [load]);

  const isChain = targetType === 'chain';
  const dirty = !!desc && !!draft && JSON.stringify(desc.settings) !== JSON.stringify(draft);
  const set = (patch: Partial<ReliabilitySettings>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setTL = (patch: Partial<ReliabilitySettings['time_limit']>) => setDraft((d) => (d ? { ...d, time_limit: { ...d.time_limit, ...patch } } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const d = await api.save(targetType, targetId, draft);
      setDesc(d);
      setDraft(d.settings);
      setNotice('Saved. New calls use these settings within about 30 seconds.');
      onSaved?.(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    setError(null);
    try {
      const d = await api.reset(targetType, targetId);
      setDesc(d);
      setDraft(d.settings);
      setNotice('Back to standard behaviour.');
      onSaved?.(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const stats = desc?.stats;
  const hasHistory = !!stats && stats.n >= (desc?.limits.min_samples || 20);
  const presets = desc?.limits.presets;
  const presetValue = (p: string) => {
    if (!presets || !stats || !hasHistory) return null;
    const cfg = presets[p as keyof typeof presets];
    const base = stats[cfg.percentile];
    return base !== undefined ? base * cfg.multiplier : null;
  };
  const hedgeBase = hasHistory && draft ? stats?.[draft.hedge.after] : null;
  const noun = isChain ? 'chain run' : 'call';
  const spreading = draft?.model_choice.mode === 'spread';
  const spreadAvailable = isChain || desc?.model_choice?.available !== false;
  const setModelChoice = (mode: 'default' | 'spread') => {
    if (!draft) return;
    if (mode === 'default') {
      set({ model_choice: { mode, models: [] } });
    } else if (isChain) {
      set({ model_choice: { mode, models: [] }, apply_to_steps: true });
    } else {
      const saved = desc?.settings.model_choice.models || [];
      set({ model_choice: { mode, models: saved.length ? saved : desc?.model_choice?.suggested || [] } });
    }
  };
  const speedLabel = (model: string) => {
    const speed = desc?.model_choice?.speeds?.[model];
    return speed?.p50_s ? `${fmt(speed.p50_s)} typical` : undefined;
  };

  const body = (
      <>
        {loading && !desc && <CircularProgress size={24} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {desc && draft && (
          <Stack spacing={3}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip size="small" label={desc.is_standard ? 'Standard' : 'Custom'} color={desc.is_standard ? 'default' : 'primary'} variant="outlined" />
              {!desc.is_standard && (
                <Typography variant="body2" color="text.secondary">
                  {summarizeReliability(desc.settings).join(' · ')}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Control how long a {noun} may take, what happens when a model fails or is slow, and which models handle the calls. Leave
              everything on the defaults to keep today's behaviour; nothing changes until you save.
            </Typography>

            <Section
              title="Time limit"
              help={
                isChain
                  ? 'Stops the whole chain run when it takes longer than it should. Time spent paused waiting for you does not count.'
                  : 'Ends the call (including any retries or backups) when it takes longer than it should.'
              }
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draft.time_limit.mode}
                onChange={(_, v) => v && setTL({ mode: v })}
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="off">No limit</ToggleButton>
                <ToggleButton value="fixed">Fixed</ToggleButton>
                <ToggleButton value="auto">Learn from history</ToggleButton>
              </ToggleButtonGroup>

              {draft.time_limit.mode === 'fixed' && (
                <TextField
                  size="small"
                  type="number"
                  label="Seconds"
                  value={draft.time_limit.seconds ?? ''}
                  onChange={(e) => setTL({ seconds: e.target.value === '' ? null : Number(e.target.value) })}
                  helperText={`Between 3 and ${Math.round(desc.limits.ceiling_s)} seconds`}
                  sx={{ width: 220 }}
                />
              )}

              {draft.time_limit.mode === 'auto' && (
                <Stack spacing={1.5}>
                  <TextField select size="small" label="Preset" value={draft.time_limit.preset} onChange={(e) => setTL({ preset: e.target.value as any })}>
                    {Object.entries(presets || {}).map(([key, cfg]) => {
                      const v = presetValue(key);
                      return (
                        <MenuItem key={key} value={key}>
                          {PRESET_LABELS[key] || key} · {cfg.percentile} × {cfg.multiplier}
                          {v !== null ? ` ≈ ${fmt(v)} now` : ''}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      type="number"
                      label="Runs to learn from"
                      value={draft.time_limit.sample_size}
                      onChange={(e) => setTL({ sample_size: Number(e.target.value) })}
                      helperText="Latest successful runs, last 30 days"
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Shortest limit (s)"
                      value={draft.time_limit.min_seconds}
                      onChange={(e) => setTL({ min_seconds: Number(e.target.value) })}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Longest limit (s)"
                      value={draft.time_limit.max_seconds ?? ''}
                      placeholder={String(Math.round(desc.limits.ceiling_s))}
                      onChange={(e) => setTL({ max_seconds: e.target.value === '' ? null : Number(e.target.value) })}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Until there's history (s)"
                      value={draft.time_limit.seconds ?? ''}
                      placeholder="No limit"
                      onChange={(e) => setTL({ seconds: e.target.value === '' ? null : Number(e.target.value) })}
                      helperText={`Used until ${desc.limits.min_samples} runs exist`}
                    />
                  </Stack>
                </Stack>
              )}

              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="body2">
                  {stats && stats.n > 0 ? (
                    <>
                      Last {stats.n} successful {isChain ? 'runs' : 'calls'}: p50 <b>{fmt(stats.p50)}</b> · p95 <b>{fmt(stats.p95)}</b> · p99{' '}
                      <b>{fmt(stats.p99)}</b> · slowest {fmt(stats.max)}
                    </>
                  ) : (
                    'No successful runs in the last 30 days yet.'
                  )}
                </Typography>
                {desc.saved && (
                  <Typography variant="body2" color="text.secondary">
                    Saved limit right now: <b>{desc.effective.time_limit_s ? fmt(desc.effective.time_limit_s) : 'none'}</b>
                    {desc.effective.time_limit_note ? ` (${desc.effective.time_limit_note})` : ''}
                    {desc.settings.time_limit.mode === 'auto' ? '. Recalculated every few minutes as new runs come in.' : ''}
                  </Typography>
                )}
              </Box>
            </Section>

            <Divider />

            <Section title={isChain ? 'When a step fails or times out' : 'When a call fails or times out'}>
              {isChain && (
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Switch
                      checked={draft.apply_to_steps}
                      onChange={(e) =>
                        set(
                          e.target.checked
                            ? { apply_to_steps: true }
                            : { apply_to_steps: false, model_choice: { mode: 'default', models: [] } },
                        )
                      }
                    />
                  }
                  label="Use these settings for every AI step that has no reliability settings of its own"
                />
              )}
              <Box sx={{ opacity: isChain && !draft.apply_to_steps ? 0.5 : 1, pointerEvents: isChain && !draft.apply_to_steps ? 'none' : 'auto' }}>
                <RadioGroup
                  value={draft.on_failure}
                  onChange={(e) => {
                    const v = e.target.value as OnFailure;
                    set({ on_failure: v, hedge: v === 'fail' ? { ...draft.hedge, enabled: false } : draft.hedge });
                  }}
                >
                  {FAILURE_OPTIONS.map((o) => (
                    <FormControlLabel
                      key={o.value}
                      value={o.value}
                      control={<Radio size="small" />}
                      sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {o.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {o.help}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>

                {(draft.on_failure === 'retry' || draft.on_failure === 'fallback') && (
                  <TextField
                    select
                    size="small"
                    label={draft.on_failure === 'retry' ? 'Retries' : 'Backup attempts'}
                    value={draft.max_retries}
                    onChange={(e) => set({ max_retries: Number(e.target.value) })}
                    sx={{ mt: 1, width: 220 }}
                  >
                    {[1, 2, 3].map((n) => (
                      <MenuItem key={n} value={n}>
                        Up to {n} more attempt{n > 1 ? 's' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {draft.on_failure === 'fallback' && (
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    <TextField
                      select
                      size="small"
                      label="Backup models"
                      value={draft.fallback_source}
                      onChange={(e) => set({ fallback_source: e.target.value as any })}
                    >
                      <MenuItem value="auto">Choose automatically: similar models, any provider, same price or cheaper</MenuItem>
                      <MenuItem value="same_provider">Choose automatically, but stay on the same provider</MenuItem>
                      <MenuItem value="custom">Use my list, in order</MenuItem>
                    </TextField>
                    {draft.fallback_source === 'custom' && (
                      <Autocomplete
                        multiple
                        size="small"
                        options={(desc.model_options || []).map((m) => m.name)}
                        value={draft.fallback_models}
                        onChange={(_, v) => set({ fallback_models: v.slice(0, 3) })}
                        renderInput={(params) => <TextField {...params} label="Backups, in order (up to 3)" />}
                      />
                    )}
                    <FormControlLabel
                      control={<Switch checked={draft.avoid_degraded} onChange={(e) => set({ avoid_degraded: e.target.checked })} />}
                      label={
                        <Box>
                          <Typography variant="body2">Skip {isChain ? "a step's" : 'the'} main model while it's degraded</Typography>
                          <Typography variant="caption" color="text.secondary">
                            If it's failing or much slower than usual right now, start on a backup instead of waiting for it to fail.
                          </Typography>
                        </Box>
                      }
                    />
                    {!isChain && desc.plan_preview && !dirty && !spreading && (
                      <Typography variant="body2" color="text.secondary">
                        Order right now: <b>{desc.plan_preview.models.join(' → ')}</b>
                        {desc.plan_preview.note ? ` (${desc.plan_preview.note})` : ''}
                      </Typography>
                    )}
                    {!isChain && dirty && (
                      <Typography variant="caption" color="text.secondary">
                        Save to see which models would be tried, in order.
                      </Typography>
                    )}
                  </Stack>
                )}

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    disabled={draft.on_failure === 'fail'}
                    control={<Switch checked={draft.hedge.enabled} onChange={(e) => set({ hedge: { ...draft.hedge, enabled: e.target.checked } })} />}
                    label={
                      <Box>
                        <Typography variant="body2">Back up slow calls</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {draft.on_failure === 'fail'
                            ? 'Needs a retry or fallback option.'
                            : `If a call is still running after its usual ${draft.hedge.after}${hedgeBase ? ` (${fmt(hedgeBase)})` : ''}, start a backup call in parallel and use whichever finishes first. Faster worst cases, but those calls can cost up to twice as much.`}
                        </Typography>
                      </Box>
                    }
                  />
                  {draft.hedge.enabled && (
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, ml: 6 }}>
                      <TextField
                        select
                        size="small"
                        label="Start the backup after"
                        value={draft.hedge.after}
                        onChange={(e) => set({ hedge: { ...draft.hedge, after: e.target.value as any } })}
                        sx={{ width: 220 }}
                      >
                        {(['p90', 'p95', 'p99'] as const).map((p) => (
                          <MenuItem key={p} value={p}>
                            {p} of recent calls{hasHistory && stats?.[p] !== undefined ? ` (${fmt(stats[p])})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                      {!hasHistory && (
                        <Typography variant="caption" color="text.secondary">
                          Until there are {desc.limits.min_samples} runs, the backup starts at half the time limit (only when a time limit is set).
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Box>
              </Box>
            </Section>

            <Divider />

            <Section
              title="Model choice"
              help={
                isChain
                  ? 'Which model handles each AI step. Spreading sends each call to a random model, so one slow or rate-limited model only touches part of your traffic.'
                  : 'Which model handles each call. Spreading sends each call to a random model from a list, so one slow or rate-limited model only touches part of your traffic.'
              }
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draft.model_choice.mode}
                onChange={(_, v) => v && setModelChoice(v)}
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="default">Default model</ToggleButton>
                <ToggleButton value="spread" disabled={!spreadAvailable}>
                  Spread across models
                </ToggleButton>
              </ToggleButtonGroup>

              {!spreadAvailable && (
                <Typography variant="body2" color="text.secondary">
                  This function only takes files (video, audio or documents), so its calls always use its own models.
                </Typography>
              )}

              {!spreading && spreadAvailable && (
                <Typography variant="body2" color="text.secondary">
                  {isChain
                    ? 'Each step uses its own model.'
                    : `Every call uses ${desc.models?.primary || 'the default model'} unless the caller asks for another model.`}
                </Typography>
              )}

              {spreading && isChain && (
                <Typography variant="body2" color="text.secondary">
                  Each AI step without settings of its own spreads its calls across its default model and similar models from other providers,
                  picked automatically for that step (similar price, same capabilities). Steps set to a different model keep it. This uses
                  the step settings above, so it turns on “Use these settings for every AI step”.
                </Typography>
              )}

              {spreading && !isChain && (
                <Stack spacing={1.5}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={(desc.model_options || [])
                      .filter((m) => !desc.models?.needs_vision || m.vision)
                      .map((m) => m.name)}
                    value={draft.model_choice.models}
                    onChange={(_, v) => set({ model_choice: { mode: 'spread', models: v.slice(0, 6) } })}
                    renderInput={(params) => <TextField {...params} label="Models to spread calls across (2 to 6)" />}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    {draft.model_choice.models.map((m) => (
                      <HealthChip key={m} model={m} health={desc.health?.[m]} role={speedLabel(m)} />
                    ))}
                    {!!desc.model_choice?.suggested.length &&
                      JSON.stringify(desc.model_choice.suggested) !== JSON.stringify(draft.model_choice.models) && (
                        <Button size="small" onClick={() => set({ model_choice: { mode: 'spread', models: desc.model_choice!.suggested } })}>
                          Use suggested ({desc.model_choice.suggested.length})
                        </Button>
                      )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Suggested: {desc.models?.primary || 'the default model'} plus the closest-priced model with the same capabilities from each
                    other provider. Each call picks one at random, skipping any that are degraded or down. Calls that name a different model
                    still use it.{' '}
                    {draft.on_failure === 'fallback'
                      ? 'If the pick fails, the rest of this list is tried first as backups.'
                      : "If the pick fails, the function's own models are the backups."}
                  </Typography>
                  {draft.time_limit.mode !== 'off' && (
                    <Typography variant="caption" color="text.secondary">
                      The time limit applies to whichever model answers. Check the typical times above: a much slower model may need a looser
                      limit.
                    </Typography>
                  )}
                </Stack>
              )}
            </Section>

            {!isChain && desc.models && (
              <>
                <Divider />
                <Section title="Model health right now" help="Error rate and speed over the last 5 minutes, across all EasyAI traffic.">
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {desc.models.allowed.map((m, i) => (
                      <HealthChip key={m} model={m} health={desc.health?.[m]} role={i === 0 ? 'main' : undefined} />
                    ))}
                    {desc.settings.model_choice.models
                      .filter((m) => !desc.models!.allowed.includes(m))
                      .map((m) => (
                        <HealthChip key={m} model={m} health={desc.health?.[m]} role="spread" />
                      ))}
                    {(desc.plan_preview?.models || [])
                      .filter((m) => !desc.models!.allowed.includes(m) && !desc.settings.model_choice.models.includes(m))
                      .map((m) => (
                        <HealthChip key={m} model={m} health={desc.health?.[m]} role="backup" />
                      ))}
                  </Stack>
                </Section>
              </>
            )}

            {desc.updated_at && (
              <Typography variant="caption" color="text.secondary">
                Last changed {new Date(desc.updated_at).toLocaleString()}
                {desc.updated_by ? ` by ${desc.updated_by}` : ''}
              </Typography>
            )}
          </Stack>
        )}
      </>
  );

  const actions = (
    <>
      <Tooltip title={desc?.saved ? 'Remove these settings and go back to standard behaviour' : 'Already standard'}>
        <span>
          <Button color="inherit" onClick={reset} disabled={saving || !desc?.saved}>
            Reset to standard
          </Button>
        </span>
      </Tooltip>
      <Box sx={{ flex: 1 }} />
      {onClose && (
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
      )}
      <Button variant="contained" onClick={save} disabled={saving || !dirty}>
        {saving ? 'Saving…' : 'Save reliability'}
      </Button>
    </>
  );

  if (layout === 'dialog') {
    return (
      <>
        <DialogContent dividers>{body}</DialogContent>
        <DialogActions>{actions}</DialogActions>
      </>
    );
  }
  return (
    <Box>
      {body}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        {actions}
      </Stack>
    </Box>
  );
};

export const ReliabilityDialog: React.FC<Props> = ({ open, onClose, targetType, targetId, targetName, actAsPublic, onSaved }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Stack direction="row" spacing={1} alignItems="center">
        <ShieldOutlinedIcon color="primary" />
        <span>Reliability{targetName ? ` · ${targetName}` : ''}</span>
      </Stack>
    </DialogTitle>
    {open && (
      <ReliabilityPanel layout="dialog" targetType={targetType} targetId={targetId} actAsPublic={actAsPublic} onSaved={onSaved} onClose={onClose} />
    )}
  </Dialog>
);

export default ReliabilityDialog;
